/**
 * Facturas.tsx — la facturación electrónica de esta empresa.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ ESTA PANTALLA NO OFRECE EMITIR, Y ES LO PRIMERO QUE HAY QUE SABER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Todavía no se puede: no hay credenciales del proveedor en ninguna parte, y
 * los emisores **se crean en el portal web y no por API** (carta `45` §2.2 del
 * hub). Un botón «Emitir» acá abriría un trámite manual disfrazado de acción.
 *
 * La regla que esto respeta la pagamos ayer, en E8.4: la pantalla de apertura
 * decía «se descarga desde el catálogo» y en el catálogo no había descarga.
 * **Una frase perfecta señalando un sitio donde no se puede hacer nada.** Así
 * que acá no se promete ningún camino que no exista.
 *
 * Lo que sí hace es contestar las dos preguntas que trae quien la abre:
 *
 *   ¿puedo facturar?   → el trámite del emisor, con su paso y lo que falta
 *   ¿qué he emitido?   → los comprobantes y en qué estado quedó cada uno
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ «NO HAY EMISOR» Y «NO HE FACTURADO» SON DOS COSAS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Y se ven iguales en una tabla vacía. La primera es un trámite sin empezar
 * —no se puede facturar— y la segunda es un trámite listo sin usar. Por eso el
 * emisor llega como `null` y no como un objeto en blanco: la pantalla no tiene
 * que adivinar cuál de sus campos significa que no existe.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ Y LOS CINCO ESTADOS SE DIBUJAN DISTINTO PORQUE SIGNIFICAN DISTINTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `aceptado` y `rechazado` son finales; `esperando_a_hacienda` no está
 * terminado y **no es un fallo**; `fallido` sí lo es y se reintenta;
 * `por_emitir` es una fila con su número tomado y a la que nadie llamó.
 *
 * Pintarlos todos igual dejaría a quien mira sin saber cuál de los cinco exige
 * hacer algo — que es la única pregunta que se hace en esta pantalla.
 */
import { useCallback, useEffect, useState } from 'react';
import { EnlaceAlAsiento } from '../componentes/EnlaceAlAsiento.tsx';
import { useIdioma, Texto } from '../nucleo/idioma.tsx';
import { useSesion } from '../nucleo/sesion.tsx';
import { mostrar } from '../nucleo/dinero.ts';
import {
  traerFacturacion, FalloDeApi,
  type Facturacion, type EstadoDeComprobante,
} from '../datos/libros.ts';
import type { Clave } from '../nucleo/idioma.ts';

/**
 * Cómo se pinta cada estado.
 *
 * `esperando_a_hacienda` va NEUTRO y no de aviso: no hay nada que hacer con él
 * y marcarlo en amarillo convierte lo normal en una alarma. La alarma es para
 * `fallido`, que sí pide algo, y para `rechazado`, que exige otro documento.
 */
const TONO: Readonly<Record<EstadoDeComprobante, string>> = {
  por_emitir: 'chip chip--soft',
  esperando_a_hacienda: 'chip chip--soft',
  aceptado: 'chip chip--ok',
  rechazado: 'chip chip--warn',
  fallido: 'chip chip--warn',
};

export function Facturas() {
  const { t, fecha: comoFecha } = useIdioma();
  const { pedir, sesion } = useSesion();
  const empresaId = sesion?.empresa.id ?? null;

  const [datos, setDatos] = useState<Facturacion | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  const mirar = useCallback(async () => {
    setFallo(null);
    try {
      setDatos(await traerFacturacion(pedir));
    } catch (e) {
      setDatos(null);
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    }
  }, [pedir]);

  useEffect(() => { if (empresaId) void mirar(); }, [empresaId, mirar]);

  const emisor = datos?.emisor ?? null;
  const recorte = datos?.comprobantes ?? null;

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>{t('fac.title')}</h1>
          <p className="page-head__sub">{t('fac.sub')}</p>
        </div>
      </div>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as never)}</span>
        </div>
      )}

      {/* ── ¿Puede facturar? ── */}
      <section className="card" style={{ marginBottom: 'var(--s6)' }}>
        <div className="card__head">
          <h3>{t('fac.issuer')}</h3>
        </div>

        {datos === null && !fallo && <p className="muted">{t('common.loading')}</p>}

        {/* ▶ Sin emisor no es «todavía no ha facturado»: es que no puede. */}
        {datos !== null && emisor === null && (
          <>
            <p><strong>{t('fac.noIssuer')}</strong></p>
            <p className="small muted" style={{ marginTop: 'var(--s4)' }}>
              <Texto k="fac.noIssuerWhy" />
            </p>
          </>
        )}

        {emisor !== null && (
          <>
            <div className="row row--tight">
              <span>
                <strong>{emisor.nombre}</strong>
                <br />
                <span className="small muted mono">
                  {emisor.identificacion}
                  {' · '}
                  {t('fac.branch', {
                    sucursal: String(emisor.sucursal), terminal: String(emisor.terminal),
                  })}
                </span>
              </span>
              <span className="spacer" />
              <span className={emisor.numeracion === 'apagado'
                ? 'chip chip--warn' : 'chip chip--ok'}>
                {t(`numera.${emisor.numeracion}` as Clave)}
              </span>
            </div>

            {/* ▶ De dónde sale el número, dicho donde se ve. Un «no puede
                emitir» sin el porqué manda a preguntar, y acá el porqué ya no
                es un trámite a medias: es que el consecutivo lo asigna
                Hacienda y Books lo anota. */}
            {emisor.numeracion !== 'books' && (
              <div className="alert alert--info" style={{ marginTop: 'var(--s5)' }}>
                <span><Texto k={`fac.step.${emisor.numeracion}` as Clave} /></span>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Qué ha emitido ── */}
      <section className="card card--pad0">
        <div className="card__head">
          <h3>{t('fac.documents')}</h3>
          <span className="spacer" />
          <span className="small muted">
            {recorte === null
              ? t('common.loading')
              : recorte.filas.length === 1
                ? t('fac.countOne')
                : t('fac.count', { n: String(recorte.filas.length) })}
          </span>
        </div>

        {/* El corte se dice, como en el diario, el mayor y la bitácora. Acá
            desde el primer día en vez de tres veces después. */}
        {recorte?.hayMas === true && (
          <div className="alert alert--warn" style={{ margin: 'var(--s5)' }}>
            <span><Texto k="fac.trimmed" con={{ limite: String(recorte.limite) }} /></span>
          </div>
        )}

        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('fac.number')}</th>
                <th>{t('fac.kind')}</th>
                <th style={{ textAlign: 'right' }}>{t('jr.total')}</th>
                <th>{t('fac.entry')}</th>
                <th>{t('fac.state')}</th>
                <th>{t('common.date')}</th>
              </tr>
            </thead>
            <tbody>
              {(recorte?.filas ?? []).map((c) => (
                <tr key={c.id}>
                  <td className="mono small">{c.consecutivo}</td>
                  <td className="small">{t(`fac.kind.${c.tipo}` as Clave)}</td>
                  <td className="mono small" style={{ textAlign: 'right' }}>
                    {/* El total sale del documento que VIAJÓ y no se recalcula.
                        `mostrar` sólo le pone la forma del idioma; la cuenta no
                        se toca en ningún punto del camino. */}
                    {c.total !== null && c.moneda !== null
                      ? mostrar({ valor: c.total, moneda: c.moneda })
                      : '—'}
                  </td>
                  {/* ▶ El asiento, y «—» cuando no lo hay — que NO es un hueco.
                      Un asiento se registra cuando el hecho económico ocurre,
                      no cuando Hacienda contesta, así que un comprobante puede
                      nacer antes que su asiento. Lo que no puede es apuntar a
                      uno que no existe: eso lo impide la 0060. */}
                  <td className="mono small">
                    {/* ▶ EL ASIENTO, Y «—» CUANDO NO LO HAY — que NO es un hueco.
                        Un asiento se registra cuando el hecho económico ocurre,
                        no cuando Hacienda contesta, así que un comprobante puede
                        nacer antes que su asiento. Lo que no puede es apuntar a
                        uno que no existe: eso lo impide la 0060.

                        ▶ El enlace estuvo, se QUITÓ, y vuelve — y las tres cosas
                        fueron correctas. Estaba como `<Link to="/diario">`
                        pelado, que abre el mes en curso: para un comprobante de
                        marzo, setiembre. La `0088` lo quitó en vez de armarlo
                        con `abierto_en`, que habría sido el mismo daño con mejor
                        letra. Vuelve ahora que la `0089` trae la fecha del
                        ASIENTO, que es la única con la que el enlace acierta.

                        Si por lo que sea no viniera la fecha, queda el número
                        sin enlace: un enlace que lleva al mes equivocado es peor
                        que ninguno, y ésa es justamente la lección de arriba. */}
                    {c.asiento === null
                      ? <span className="muted">—</span>
                      : c.asiento_fecha === null
                        ? <span>#{c.asiento}</span>
                        : <EnlaceAlAsiento asiento={c.asiento} fecha={c.asiento_fecha} />}
                  </td>
                  <td className="small">
                    <span className={TONO[c.estado]}>{t(`estado.${c.estado}` as Clave)}</span>
                    {/* El motivo, cuando lo hay. Un rechazo sin motivo obliga a
                        preguntarle al proveedor por algo que ya contestó. */}
                    {c.motivo && (
                      <>
                        <br />
                        <span className="small muted">{c.motivo}</span>
                      </>
                    )}
                    {/* ▶ Y de quién vino la respuesta. `simulado` queda escrito
                        para siempre: un simulador que se hace pasar por el
                        proveedor es la forma más rápida de que alguien crea que
                        ya está facturando. */}
                    {c.proveedor && (
                      <span className="chip chip--soft" style={{ marginLeft: 8 }}>
                        {c.proveedor}
                      </span>
                    )}
                  </td>
                  <td className="mono small">
                    {comoFecha((c.resuelto_en ?? c.abierto_en).slice(0, 10))}
                  </td>
                </tr>
              ))}

              {recorte !== null && recorte.filas.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 'var(--s7)' }}>
                    {/* Las dos formas de estar vacía, otra vez: sin emisor no se
                        puede facturar; con emisor listo, todavía no se ha. */}
                    {emisor === null ? t('fac.emptyNoIssuer') : t('fac.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <p className="field__hint" style={{ marginTop: 'var(--s6)' }}>{t('fac.why')}</p>
    </>
  );
}
